import useUrlState from '@ahooksjs/use-url-state'
import { LeftOutlined, RightOutlined } from '@ant-design/icons'
import { useRequest, useTitle } from 'ahooks'
import { Progress, Spin, Tabs } from 'antd'
import { useAtom } from 'jotai'
import { assign, merge, round } from 'lodash'
import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'

import {
  DepartmentTree,
  Favorite,
  LockIcon,
  NoPermissionPage,
  PERMISSIONS,
  hasPointPagePermission,
  useDepartmentTree,
} from '@/components'
import { ReportContainer } from '@/components/reports/report.container'
import { Header } from '@/modules/header'
import { NormalResourceList } from '@/modules/portfolio/portfolio-list'
import { userInfoAtom } from '@/modules/user'
import { PAGE_RESOURCE_POINTS_ENUM, ResourceType, ResourceUser, Team } from '@/schemas'
import { ga, traceEvent } from '@/utils/ga'

import { TeamUserResourceDashboard } from './team-dashboard'
import { TeamOKR } from './team-okr'
import { WorkResourceContainer } from './work-resource/work-resource.container'

const { TabPane } = Tabs

export const TeamContainer = () => {
  const { t, i18n } = useTranslation()
  const [userInfo] = useAtom(userInfoAtom)

  const reportRef = React.useRef<{ entryTime: number; name?: string; entry?: string; users?: ResourceUser[] }>()
  const headerRef = React.useRef<HTMLDivElement>(null)
  const tabRef = React.useRef<HTMLDivElement>(null)

  const [users, setUsers] = React.useState<ResourceUser[]>()
  const [prevState, setPrevState] = React.useState<{ tab?: string; departmentId?: number }>({})

  const [state, setState] = useUrlState(
    { tab: 'goal', departmentId: userInfo!.departmentIds[0] },
    {
      navigateMode: 'replace',
    }
  )

  const { treeData, department, parentIds, departmentId } = useDepartmentTree({
    currentDepartmentId: Number(state.departmentId),
  })

  const name = i18n.resolvedLanguage === 'zh' ? department?.name : department?.ename
  useTitle(name || t('team management'), { restoreOnUnmount: true })

  const [progress, setProgress] = useState(0)
  const [isHideTree, setIsHideTree] = useState(false)

  const { data: team } = useRequest<Team>(
    {
      url: '/api/team/getTeamById',
      params: {
        departmentId,
      },
    },
    {
      ready: !!departmentId,
      refreshDeps: [departmentId],
    }
  )

  const reportExposeTime = () => {
    const report = reportRef.current
    if (report && report.name && report.entryTime) {
      ga('event', traceEvent.detailPage.events.expose_time_teams, {
        event_label: report.name,
        event_value: Math.round((+new Date() - report.entryTime) / 1000),
        event_category: 'ef_pmo_teams',
        action: traceEvent.detailPage.action,
        target: traceEvent.detailPage.targets.ef_pmo_teams,
        payload: report.users
          ? report.users.some((user: ResourceUser) => user.email === userInfo.email)
            ? '1'
            : '0'
          : '',
      })
    }
  }

  React.useEffect(() => {
    ga('event', traceEvent.navigation.events.show_navigation, {
      event_category: traceEvent.navigation.event_categories.ef_pmo,
      event_label: traceEvent.navigation.event_labels.teams,
      action: traceEvent.navigation.action,
    })

    reportRef.current = { entryTime: +new Date() }
    window.addEventListener('beforeunload', reportExposeTime)
    return () => {
      window.removeEventListener('beforeunload', reportExposeTime)
      reportExposeTime()
    }
  }, [departmentId])

  React.useEffect(() => {
    if (team) {
      reportRef.current = assign(reportRef.current, { name: team.name })
    }
  }, [team])

  React.useEffect(() => {
    if (users) {
      reportRef.current = assign(reportRef.current, { users })
    }
  }, [users])

  React.useEffect(() => {
    if (team && (team.departmentId !== prevState.departmentId || state.tab !== prevState.tab)) {
      switch (state.tab) {
        case 'goal':
          ga('event', traceEvent.teamTab.events.click_switch_tab, {
            event_category: traceEvent.teamTab.event_categories.ef_pmo_teams,
            action: traceEvent.teamTab.action,
            event_label: team.name,
            target: traceEvent.teamTab.targets.objectives,
          })
          break
        case 'userResource':
          ga('event', traceEvent.teamTab.events.click_switch_tab, {
            event_category: traceEvent.teamTab.event_categories.ef_pmo_teams,
            action: traceEvent.teamTab.action,
            event_label: team.name,
            target: traceEvent.teamTab.targets.resource,
          })
          break
        case 'portfolio':
          ga('event', traceEvent.teamTab.events.click_switch_tab, {
            event_category: traceEvent.teamTab.event_categories.ef_pmo_teams,
            action: traceEvent.teamTab.action,
            event_label: team.name,
            target: traceEvent.teamTab.targets.projects_portfolios,
          })
          break
        case 'report':
          ga('event', traceEvent.teamTab.events.click_switch_tab, {
            event_category: traceEvent.teamTab.event_categories.ef_pmo_teams,
            action: traceEvent.teamTab.action,
            event_label: team.name,
            target: traceEvent.teamTab.targets.weekly_reports,
          })
          break
      }
      setPrevState({ tab: state.tab, departmentId: team.departmentId })
    }
  }, [state, team])

  return (
    <>
      <div style={{ position: 'sticky', top: 0, zIndex: 3, background: 'white' }} ref={headerRef}>
        <Header
          title={department && `- ${name}`}
          leftExtra={
            <>
              <div>{team && <Favorite selfId={team.selfId} type={team.type} isFavourited={team.isFavourited} />}</div>
              <Progress className='font-mono flex-shrink-0' type='circle' width={26} percent={round(progress, 0)} />
            </>
          }
          resourceType={ResourceType.TEAM}
        />
      </div>
      <div className='flex overflow-hidden'>
        <div
          className={`fixed flex-shrink-0 border-0 border-r-1 border-solid border-light-700 ${isHideTree ? 'w-0' : ''}`}
        >
          <div className='bg-white border-solid flex border-1 border-light-700 rounded-1/2 h-24px top-12px -right-12px text-light-900 w-24px absolute items-center justify-center'>
            {isHideTree ? (
              <RightOutlined onClick={() => setIsHideTree(!isHideTree)} />
            ) : (
              <LeftOutlined onClick={() => setIsHideTree(!isHideTree)} />
            )}
          </div>
          <div
            style={{ height: `calc(100vh - ${headerRef.current?.getBoundingClientRect().height || 0}px)` }}
            className='overflow-y-auto'
          >
            <DepartmentTree
              includeRD={true}
              showSearch={true && !isHideTree}
              treeData={treeData}
              selectedKeys={parentIds}
              onChangeKey={(key) => setState({ ...state, departmentId: key })}
            />
          </div>
        </div>
        <div
          className={`${
            isHideTree ? 'ml-0px' : 'ml-260px'
          } w-full pl-20px overflow-hidden flex-auto min-w-0 transition-left duration-50 ease-linear`}
        >
          <div ref={tabRef}>
            <Tabs
              activeKey={state.tab}
              onChange={(tab) => {
                const keys = Object.keys(state)
                setState({
                  ...merge({}, ...keys.map((key) => ({ [key]: undefined }))), // reset url params
                  tab,
                  departmentId: state.departmentId,
                })
              }}
              style={{ marginBottom: 0 }}
              destroyInactiveTabPane
            >
              <TabPane
                tab={
                  <span>
                    {t('objectives')}
                    {!hasPointPagePermission(
                      PAGE_RESOURCE_POINTS_ENUM.目标,
                      PERMISSIONS.READ,
                      team ? team.permissions : null
                    ) && <LockIcon />}
                  </span>
                }
                key='goal'
              ></TabPane>
              <TabPane
                tab={
                  <span>
                    {t('resource dashboard')}
                    {!hasPointPagePermission(
                      PAGE_RESOURCE_POINTS_ENUM.资源看板,
                      PERMISSIONS.READ,
                      team ? team.permissions : null
                    ) && <LockIcon />}
                  </span>
                }
                key='dashboard'
              ></TabPane>
              <TabPane
                tab={
                  <span>
                    {t('user resource')}
                    {!hasPointPagePermission(
                      PAGE_RESOURCE_POINTS_ENUM.资源管理,
                      PERMISSIONS.READ,
                      team ? team.permissions : null
                    ) && <LockIcon />}
                  </span>
                }
                key='userResource'
              ></TabPane>
              {/* <TabPane
                tab={
                  <span>
                    {t('projects/portfolios.name')}
                    {!hasPointPagePermission(
                      PAGE_RESOURCE_POINTS_ENUM['项目/组合'],
                      PERMISSIONS.READ,
                      team ? team.permissions : null
                    ) && <LockIcon />}
                  </span>
                }
                key='portfolio'
              ></TabPane> */}
              <TabPane
                tab={
                  <span>
                    {t('reports')}
                    {/* {!hasPointPagePermission(
                      PAGE_RESOURCE_POINTS_ENUM.周报,
                      PERMISSIONS.READ,
                      team ? team.permissions : null
                    ) && <LockIcon />} */}
                  </span>
                }
                key='report'
              ></TabPane>
            </Tabs>
          </div>

          {state.tab === 'goal' && team && (
            <TeamOKR team={team} departmentId={departmentId} setProgress={setProgress} setUsers={setUsers} />
          )}
          {state.tab === 'dashboard' &&
            team &&
            (hasPointPagePermission(PAGE_RESOURCE_POINTS_ENUM.资源看板, PERMISSIONS.READ, team.permissions) ? (
              <TeamUserResourceDashboard departmentId={departmentId} team={team} />
            ) : (
              <NoPermissionPage resourcePoint={PAGE_RESOURCE_POINTS_ENUM.资源看板} />
            ))}
          {state.tab === 'userResource' &&
            team &&
            (hasPointPagePermission(PAGE_RESOURCE_POINTS_ENUM.资源管理, PERMISSIONS.READ, team.permissions) ? (
              <WorkResourceContainer team={team} />
            ) : (
              <NoPermissionPage resourcePoint={PAGE_RESOURCE_POINTS_ENUM.资源管理} />
            ))}
          {state.tab === 'portfolio' &&
            team &&
            (hasPointPagePermission(PAGE_RESOURCE_POINTS_ENUM['项目/组合'], PERMISSIONS.READ, team.permissions) ? (
              <Spin spinning={!team}>
                <NormalResourceList resource={team} pageType={ResourceType.TEAM} defaultFilter={[2, 3, 5]} />
              </Spin>
            ) : (
              <NoPermissionPage resourcePoint={PAGE_RESOURCE_POINTS_ENUM['项目/组合']} />
            ))}
          {state.tab === 'report' && team && (
            <Spin spinning={!team}>
              {
                <ReportContainer
                  selfId={team.selfId}
                  type={team.type}
                  permissions={team.permissions}
                  name={team.name}
                />
              }
            </Spin>
          )}
        </div>
      </div>
    </>
  )
}
